<template>
  <eg-modal
    id="workspace-configuration-modal"
    :title="$t('dashboard.workspace.organize')"
    size="large"
    :closable="true"
    :is-lost-data-modal="isDirty"
    :lost-data-warning="lostDataWarning"
  >
    <!-- Custom Header with Subtitle -->
    <template #header="{ close }">
      <eg-modal-header
        :title="$t('dashboard.workspace.organize')"
        closable
        @onClose="handleCancel(close)"
      >
        <template #title="{ title }">
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center">
              <h3 class="semi-bold eg-modal__title">{{ title }}</h3>
              <div class="workspace-header__breadcrumb ml-3">
                {{ workspaceTypeTitle }}
              </div>
            </div>
            <div class="workspace-header__subtitle mt-2">
              {{ $t("dashboard.nameYourFolders") }}
            </div>
          </div>
        </template>
      </eg-modal-header>
    </template>

    <!-- Modal Body -->
    <div class="workspace-configuration">
      <div class="configuration-layout">
        <!-- Left side - Preview Structure -->
        <PreviewTree :nodes="previewNodes" :selected-node.sync="selectedNode" />
        <!-- Right side - Subjects/Classes/Assignments Builder -->
        <div class="configuration-panel">
          <div class="subjects-section">
            <div
              class="section-header d-flex align-items-center justify-content-between"
            >
              <h3 class="section-title">{{ labels.level1Plural }}</h3>
              <eg-button
                type="secondary"
                size="small"
                icon="plus-add-more-maximize"
                @click="addSubject"
              >
                {{ labels.addLevel1 }}
              </eg-button>
            </div>
            <level-one-list
              v-model="subjects"
              :workspace-type="workspaceType"
              :labels="labels"
              :placeholders="placeholders"
              :has-validation-error="hasValidationError"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #footer="{ close }">
      <div
        class="workspace-configuration__footer d-flex justify-content-end align-items-center"
      >
        <div v-if="saveWarning" class="save-warning mr-auto">
          {{ saveWarning }}
        </div>
        <eg-button type="secondary" size="medium" @click="handleBack(close)">
          {{ $t("common.back") }}
        </eg-button>
        <eg-button
          id="create-workspace-btn"
          type="primary"
          size="medium"
          :is-loading="isSaving"
          :loading-text="$t('dashboard.workspace.saving')"
          @click="handleCreateWorkspace(close)"
        >
          {{ $t("dashboard.workspace.createWorkspace") }}
        </eg-button>
      </div>
    </template>
  </eg-modal>
</template>

<script>
import EgModalHeader from "@/components/global/eg/modals/eg_modal_header.vue";
import PreviewTree from "./components/PreviewTree.vue";
import { addFolder } from "@/api/requests/folder";
import { uiToBatchFolders } from "./utils/mappers";
import { mapToPreviewNodes, generateId } from "./utils/workspace-helpers";
import LevelOneList from "./components/LevelOneList.vue";
import {
  WORKSPACE_TYPES,
  DEFAULT_UI_STRUCTURES,
  WORKSPACE_LABELS,
  WORKSPACE_PLACEHOLDERS,
} from "@/constants";

export default {
  name: "WorkspaceConfiguration",
  components: {
    EgModalHeader,
    LevelOneList,
    PreviewTree,
  },
  props: {
    workspaceType: {
      type: String,
      default: null,
    },
    // Optional seed from parent cache: server-ready payload array [{ name, children: [...] }]
    initialStructure: {
      type: Array,
      default: null,
    },
  },
  emits: ["back", "workspace-created", "cancel"],
  data() {
    return {
      selectedNode: null,
      originalServerPayload: null,
      isSaving: false,
      saveWarning: "",
      subjects: [], // Initialize as empty, will be set in created hook
      nextId: 2,
      showValidationErrors: false, // Simple flag to show validation errors
      isDirty: false,
    };
  },
  computed: {
    lostDataWarning() {
      return {
        text: this.$t("dashboard.workspace.loseStepProgress"),
        okTitle: this.$t("dashboard.confirmation.leaveStay"),
      };
    },
    workspaceTypeTitle() {
      const type = this.workspaceType || "subject";
      return this.$t(
        WORKSPACE_TYPES[type]?.title ||
          "dashboard.workspace.types.subject.title"
      );
    },
    labels() {
      const type = this.workspaceType || "subject";
      const raw = WORKSPACE_LABELS[type];
      return Object.fromEntries(
        Object.entries(raw).map(([k, v]) => [k, this.$t(v)])
      );
    },
    placeholders() {
      const type = this.workspaceType || "subject";
      const raw = WORKSPACE_PLACEHOLDERS[type];
      return Object.fromEntries(
        Object.entries(raw).map(([k, v]) => [k, this.$t(v)])
      );
    },

    previewNodes() {
      return mapToPreviewNodes(this.subjects);
    },
    defaultStructure() {
      // Use pre-filled data from constants.js
      const type = this.workspaceType || "subject";
      return JSON.parse(JSON.stringify(DEFAULT_UI_STRUCTURES[type]));
    },
  },
  watch: {
    initialStructure: {
      handler(val) {
        if (Array.isArray(val)) {
          this.originalServerPayload = JSON.parse(JSON.stringify(val));
          this.subjects = this.hydrateUiFromPayload(val);
          this.updatePreview();
        }
      },
      deep: true,
    },

    subjects: {
      handler(val) {
        if (
          JSON.stringify(val) !==
          JSON.stringify(DEFAULT_UI_STRUCTURES[this.workspaceType || "subject"])
        ) {
          this.isDirty = true;
        } else {
          this.isDirty = false;
        }
      },
      deep: true,
    },
  },

  mounted() {
    if (Array.isArray(this.initialStructure)) {
      this.originalServerPayload = JSON.parse(
        JSON.stringify(this.initialStructure)
      );
      this.subjects = this.hydrateUiFromPayload(this.initialStructure);
    } else {
      this.subjects = this.defaultStructure;
    }
  },

  methods: {
    hasValidationError(level, l1Index, l2Index = null, l3Index = null) {
      if (!this.showValidationErrors) return false;

      const item =
        level === 1
          ? this.subjects[l1Index]
          : level === 2
          ? this.subjects[l1Index]?.children?.[l2Index]
          : this.subjects[l1Index]?.children?.[l2Index]?.children?.[l3Index];

      return !item?.name || item.name.trim() === "";
    },

    hydrateUiFromPayload(payload) {
      const mapLevel3 = this.mapAssignments;
      const mapLevel2 = this.mapClasses(mapLevel3);
      return (payload || []).map((l1) => ({
        id: l1?.id != null ? l1.id : generateId(),
        name: l1?.name || "",
        expanded: true,
        children: mapLevel2(l1?.children || []),
      }));
    },
    mapAssignments(assignments = []) {
      return (assignments || []).map((a) => ({
        id: a?.id != null ? a.id : generateId(),
        name: a?.name || "",
      }));
    },
    mapClasses(mapLevel3) {
      return (children = []) =>
        (children || []).map((l2) => ({
          id: l2?.id != null ? l2.id : generateId(),
          name: l2?.name || "",
          canNotExpand: this.workspaceType === "own",
          expanded: true,
          children: mapLevel3(l2?.children || []),
        }));
    },

    // Subject management
    addSubject() {
      this.subjects.push({
        id: this.nextId++,
        name: "",
        expanded: true,
        children: [],
      });
      this.updatePreview();
    },
    updatePreview() {
      // Force reactivity update
      this.$forceUpdate();
    },

    // Modal actions
    handleBack(close) {
      this.showValidationErrors = false;
      // Emit server-ready payload to cache in parent
      this.$emit("back", {
        type: this.workspaceType || "subject",
        configuration: uiToBatchFolders(this.subjects),
      });
      close();
    },
    handleCreateWorkspace(close) {
      if (this.isSaving) return;
      const validationMessage = this.validateStructure();

      if (validationMessage) {
        this.saveWarning = validationMessage;
        return;
      }

      this.saveWarning = "";
      this.isSaving = true;
      // One-time batch create with full structure
      const folders = uiToBatchFolders(this.subjects);
      addFolder({ folders })
        .then((response) => {
          this.showValidationErrors = false;
          this.$emit("workspace-created", {
            type: this.workspaceType || "subject",
            configuration: response.data,
          });
          // Clear temporary workspaces from localStorage
          localStorage.removeItem("workspace_creation_progress");
          this.isSaving = false;
          close(false);
        })
        .catch((e) => {
          console.error("Failed to create folders batch", e);
          this.isSaving = false;
        });
    },

    validateStructure() {
      this.showValidationErrors = true;

      // Check empty names
      const hasEmptyNames = this.subjects.some(
        (l1) =>
          !l1.name?.trim() ||
          l1.children?.some(
            (l2) =>
              !l2.name?.trim() || l2.children?.some((l3) => !l3.name?.trim())
          )
      );

      if (hasEmptyNames) {
        return this.$t("common.pleaseFillNames");
      }

      // Check structure
      const hasEmptyStructure =
        this.subjects.some((l1) => !l1.children?.length) ||
        (this.workspaceType !== "own" &&
          this.subjects
            .flatMap((l1) => l1.children || [])
            .some((l2) => l2.canNotExpand || !l2.children?.length));

      if (hasEmptyStructure) {
        return this.$t("dashboard.workspace.eachClassMustHaveAssignment");
      }

      return null;
    },

    async handleCancel(close) {
      const leave = await close();
      if (leave) {
        this.showValidationErrors = false;
        this.$emit("cancel");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.workspace-header {
  display: flex;
  flex-direction: column;

  &__top {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__title {
    color: var(--color-text-neutral-on-white);
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    margin: 0;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;
  }

  &__breadcrumb {
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 500;
    background: var(--color-surface-neutral-container-2);
    padding: 4px 8px;
    border-radius: 16px;
  }

  &__subtitle {
    color: var(--color-text-neutral-secondary);
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;
  }
}

.workspace-configuration {
  padding: 0;

  &__footer {
    gap: 12px;
  }
}

.save-warning {
  color: var(--color-text-semantic-onWarning);
  font-size: 14px;
}

.configuration-layout {
  display: grid;
  grid-template-columns: 328px 1fr;
  height: 600px;
}

.configuration-panel {
  overflow-y: auto;
  max-height: 600px;
  scrollbar-width: thin;
}

.subjects-section {
  display: flex;
  flex-direction: column;
  ::v-deep {
    .drag-handle {
      width: 20px;
      height: 36px;
      line-height: 36px;
    }
  }
}

.section-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--color-stroke-neutral-default);
}

.section-title {
  color: var(--color-text-neutral-on-white);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  margin: 0;
  font-family: Inter, sans-serif;
}

// Responsive
@media (max-width: 1024px) {
  .configuration-layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .configuration-panel {
    order: 1;
    max-height: none;
  }
}
</style>
